/**
 * Gender-Responsive Emoji Utility
 * Returns appropriate emojis based on user's gender setting
 * Defaults to female emojis for backwards compatibility
 */

// Emoji mappings for different genders
const GENDER_EMOJIS = {
  // Activity/Exercise emojis
  running: {
    female: "🏃🏾‍♀️",
    male: "🏃🏾‍♂️",
    neutral: "🏃🏾"
  },
  yoga: {
    female: "🧘🏾‍♀️",
    male: "🧘🏾‍♂️",
    neutral: "🧘🏾"
  },
  cycling: {
    female: "🚴🏾‍♀️",
    male: "🚴🏾‍♂️",
    neutral: "🚴🏾"
  },
  swimming: {
    female: "🏊🏾‍♀️",
    male: "🏊🏾‍♂️",
    neutral: "🏊🏾"
  },
  walking: {
    female: "🚶🏾‍♀️",
    male: "🚶🏾‍♂️",
    neutral: "🚶🏾"
  },
  lifting: {
    female: "🏋🏾‍♀️",
    male: "🏋🏾‍♂️",
    neutral: "🏋🏾"
  },
  gymnastics: {
    female: "🤸🏾‍♀️",
    male: "🤸🏾‍♂️",
    neutral: "🤸🏾"
  },
  
  // Person emojis
  person: {
    female: "👩🏾",
    male: "👨🏾",
    neutral: "🧑🏾"
  },
  personRaising: {
    female: "🙋🏾‍♀️",
    male: "🙋🏾‍♂️",
    neutral: "🙋🏾"
  },
  personTipping: {
    female: "💁🏾‍♀️",
    male: "💁🏾‍♂️",
    neutral: "💁🏾"
  },
  personGesturing: {
    female: "🤷🏾‍♀️",
    male: "🤷🏾‍♂️",
    neutral: "🤷🏾"
  },
  personFrowning: {
    female: "🙍🏾‍♀️",
    male: "🙍🏾‍♂️",
    neutral: "🙍🏾"
  },
  personPouting: {
    female: "🙎🏾‍♀️",
    male: "🙎🏾‍♂️",
    neutral: "🙎🏾"
  },
  
  // Health/Wellness emojis
  massage: {
    female: "💆🏾‍♀️",
    male: "💆🏾‍♂️",
    neutral: "💆🏾"
  },
  haircut: {
    female: "💇🏾‍♀️",
    male: "💇🏾‍♂️",
    neutral: "💇🏾"
  },
  doctor: {
    female: "👩🏾‍⚕️",
    male: "👨🏾‍⚕️",
    neutral: "🧑🏾‍⚕️"
  },
  chef: {
    female: "👩🏾‍🍳",
    male: "👨🏾‍🍳",
    neutral: "🧑🏾‍🍳"
  },
  teacher: {
    female: "👩🏾‍🏫",
    male: "👨🏾‍🏫",
    neutral: "🧑🏾‍🏫"
  },
  
  // Crown/Royalty
  crown: {
    female: "👸🏾",
    male: "🤴🏾",
    neutral: "👑"
  },
  
  // Strength/Power
  strength: {
    female: "💪🏾",
    male: "💪🏾",
    neutral: "💪🏾"
  },
  
  // Athletic body types
  athletic: {
    female: "🏃🏾‍♀️",
    male: "🏃🏾‍♂️",
    neutral: "🏃🏾"
  }
};

/**
 * Get gender-appropriate emoji
 * @param {string} emojiType - The type of emoji (e.g., 'running', 'yoga', 'person')
 * @param {string} gender - User's gender setting ('female', 'male', 'non_binary', 'prefer_not_say')
 * @returns {string} The appropriate emoji
 */
export const getGenderEmoji = (emojiType, gender) => {
  const emojiSet = GENDER_EMOJIS[emojiType];
  if (!emojiSet) return "✨"; // Default fallback
  
  switch (gender) {
    case "male":
      return emojiSet.male;
    case "female":
      return emojiSet.female;
    case "non_binary":
    case "prefer_not_say":
      return emojiSet.neutral;
    default:
      return emojiSet.female; // Default to female for backwards compatibility
  }
};

/**
 * Get all activity emojis for a given gender
 * @param {string} gender - User's gender setting
 * @returns {object} Object with all activity emojis
 */
export const getActivityEmojis = (gender) => ({
  running: getGenderEmoji("running", gender),
  yoga: getGenderEmoji("yoga", gender),
  cycling: getGenderEmoji("cycling", gender),
  swimming: getGenderEmoji("swimming", gender),
  walking: getGenderEmoji("walking", gender),
  lifting: getGenderEmoji("lifting", gender),
  gymnastics: getGenderEmoji("gymnastics", gender)
});

/**
 * Get all person emojis for a given gender
 * @param {string} gender - User's gender setting
 * @returns {object} Object with all person emojis
 */
export const getPersonEmojis = (gender) => ({
  person: getGenderEmoji("person", gender),
  raising: getGenderEmoji("personRaising", gender),
  tipping: getGenderEmoji("personTipping", gender),
  gesturing: getGenderEmoji("personGesturing", gender),
  doctor: getGenderEmoji("doctor", gender),
  chef: getGenderEmoji("chef", gender),
  massage: getGenderEmoji("massage", gender),
  crown: getGenderEmoji("crown", gender)
});

export default GENDER_EMOJIS;
